import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useAtomValue } from 'jotai/utils';
import { chatSessionIdAtom } from 'src/shared/chat';
import { useRollbackChatOpMutation } from 'src/api/mutations/chatMutations';
import type { RollbackHttpResult, RollbackSuccessResult, UIAction } from './types';

// Shared, side-effect-light rollback runner used by both the per-op Undo
// button on `ActionCard` and the bulk "Undo all" button on `BatchActionCard`.
//
// Why it lives here instead of inline in `ActionCard`:
//   The bulk caller fans this out N times (concurrency-capped) and rolls
//   per-op results into one aggregate toast. Keeping the per-op success
//   mapping, error code branching, and per-asset cache invalidation in one
//   place stops the two undo flows from diverging (the metadata-rollback
//   handoff doc lists the exact status/code → UX mapping the server returns).
//
// The hook intentionally does NOT show toasts. Callers decide whether to
// emit per-op feedback (`ActionCard`) or one aggregate toast at the end of
// a batch (`BatchActionCard`). Per-asset `['asset', id]` cache invalidation
// is fired here because it's free under the rAF coalescer in
// `useUIActionDispatcher`, and skipping it for the bulk case would mean
// some asset cards stay stale after Undo all.

export type RollbackOutcome =
  | {
      kind: 'reverted';
      result: RollbackSuccessResult;
      uiAction?: UIAction;
      successCount: number;
      partialFailureCount: number;
      total: number;
      unremovableKeys: string[];
    }
  // 400 op_not_found_or_expired or op_already_consumed: nothing to do, button
  // should mark itself reverted/disabled.
  | {
      kind: 'expired';
      reason: 'op_not_found_or_expired' | 'op_already_consumed';
      message: string;
    }
  | { kind: 'auth'; message: string }
  | { kind: 'forbidden'; message: string }
  // 502/503/5xx — retryable. The button should stay enabled for the user.
  | { kind: 'failed'; message: string };

// Maps one rollback HTTP result to the outcome the cards render. Pure, so
// the status/code -> UX table is unit-testable without React Query.
export function classifyRollbackResult(result: RollbackHttpResult): RollbackOutcome {
  if (result.ok) {
    const { result: r, ui_action } = result.body;
    const total = r.asset_results?.length || 0;
    const failed = (r.asset_results || []).filter((a) => !a.success).length;
    // A 200 in which no asset was restored undid nothing (a proxy predating
    // the 502 for this case). Retryable: the record is kept for a retry.
    if (total > 0 && failed === total) {
      return { kind: 'failed', message: NOTHING_UNDONE_MESSAGE };
    }
    return {
      kind: 'reverted',
      result: r,
      uiAction: ui_action,
      successCount: total - failed,
      partialFailureCount: failed,
      total,
      unremovableKeys: r.unremovable_keys || [],
    };
  }

  const { status, body } = result;
  switch (true) {
    case status === 400 && body.code === 'op_not_found_or_expired':
      return {
        kind: 'expired',
        reason: 'op_not_found_or_expired',
        message: 'This change is too old to undo (24-hour limit).',
      };
    case status === 400 && body.code === 'op_already_consumed':
      return {
        kind: 'expired',
        reason: 'op_already_consumed',
        message: 'Already reverted.',
      };
    case status === 401:
      return { kind: 'auth', message: 'Session expired. Please log in again.' };
    case status === 403:
      return {
        kind: 'forbidden',
        message: "You don't have permission to undo this change.",
      };
    case status === 502 && body.code === 'rollback_failed':
      return {
        kind: 'failed',
        message: body.message || "Couldn't undo this change. Please try again.",
      };
    case status === 503 && body.code === 'rollback_unavailable':
      return { kind: 'failed', message: 'Undo is temporarily unavailable.' };
    default:
      return { kind: 'failed', message: "Couldn't undo this change. Please try again." };
  }
}

const NOTHING_UNDONE_MESSAGE = "Couldn't undo this change. Nothing was changed; please try again.";

export function useRollbackRunner() {
  const sessionId = useAtomValue(chatSessionIdAtom);
  const queryClient = useQueryClient();
  const rollbackMutation = useRollbackChatOpMutation();

  return useCallback(
    async (opId: string): Promise<RollbackOutcome> => {
      const result = await rollbackMutation.mutateAsync({
        opId,
        sessionId: sessionId || undefined,
      });
      const outcome = classifyRollbackResult(result);
      if (outcome.kind === 'reverted') {
        // Per-asset cache invalidation: under the rAF coalescer in
        // useUIActionDispatcher these collapse to a single flush.
        (outcome.result.asset_results || []).forEach((a) =>
          queryClient.invalidateQueries(['asset', a.asset_id]),
        );
      }
      return outcome;
    },
    [rollbackMutation, sessionId, queryClient],
  );
}

// Helpers used by both ActionCard (single op) and BatchActionCard
// (aggregate). Building these here keeps the wording consistent across both
// paths.

export function buildUnremovableNote(keys: string[]): string | null {
  if (keys.length === 0) return null;
  const plural = keys.length === 1 ? '' : 's';
  const verb = keys.length === 1 ? 'was' : 'were';
  return `Note: ${keys.length} newly-added field${plural} could not be removed and ${verb} cleared to empty. (${keys.join(', ')})`;
}
